/**
 * Copyright 2024-2025, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { ProjectConfigManagerImpl } from "./project_config_manager";
import { PollingDatafileManager } from "./polling_datafile_manager";
import { DEFAULT_UPDATE_INTERVAL } from './constant';
import { ExponentialBackoff, IntervalRepeater } from "../utils/repeater/repeater";
import { MIN_UPDATE_INTERVAL, UPDATE_INTERVAL_BELOW_MINIMUM_MESSAGE } from './constant';
import { LogLevel } from '../logging/logger';
import { validateStore } from "../utils/cache/store_validator";
export const INVALID_CONFIG_MANAGER = "Invalid config manager";
const configManagerSymbol = Symbol();
export const createStaticProjectConfigManager = (config) => {
    return {
        [configManagerSymbol]: new ProjectConfigManagerImpl(config),
    };
};
export const getPollingConfigManager = (opt) => {
    var _a;
    if (opt.cache) {
        validateStore(opt.cache);
    }
    const updateInterval = (_a = opt.updateInterval) !== null && _a !== void 0 ? _a : DEFAULT_UPDATE_INTERVAL;
    const backoff = new ExponentialBackoff(1000, updateInterval, 500);
    const repeater = new IntervalRepeater(updateInterval, backoff);
    const startupLogs = [];
    if (updateInterval < MIN_UPDATE_INTERVAL) {
        startupLogs.push({
            level: LogLevel.Warn,
            message: UPDATE_INTERVAL_BELOW_MINIMUM_MESSAGE,
            params: [],
        });
    }
    const datafileManagerConfig = {
        sdkKey: opt.sdkKey,
        autoUpdate: opt.autoUpdate,
        urlTemplate: opt.urlTemplate,
        datafileAccessToken: opt.datafileAccessToken,
        customHeaders: opt.customHeaders,
        requestHandler: opt.requestHandler,
        cache: opt.cache,
        repeater,
        startupLogs,
    };
    const datafileManager = new PollingDatafileManager(datafileManagerConfig);
    return new ProjectConfigManagerImpl({
        datafile: opt.datafile,
        datafileManager,
        jsonSchemaValidator: opt.jsonSchemaValidator,
    });
};
export const getOpaquePollingConfigManager = (opt) => {
    return {
        [configManagerSymbol]: getPollingConfigManager(opt),
    };
};
export const wrapConfigManager = (configManager) => {
    return {
        [configManagerSymbol]: configManager,
    };
};
export const extractConfigManager = (opaqueConfigManager) => {
    if (!opaqueConfigManager || typeof opaqueConfigManager !== 'object') {
        throw new Error(INVALID_CONFIG_MANAGER);
    }
    const configManager = opaqueConfigManager[configManagerSymbol];
    if (!configManager) {
        throw new Error(INVALID_CONFIG_MANAGER);
    }
    return opaqueConfigManager[configManagerSymbol];
};
export const __platforms = ['__universal__'];
//# sourceMappingURL=config_manager_factory.js.map